import { CommonModule } from '@angular/common';
import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { BarcodeFormat, BarcodeScanner } from '@capacitor-mlkit/barcode-scanning';
import { Capacitor, PluginListenerHandle } from "@capacitor/core";
import { Haptics, ImpactStyle } from '@capacitor/haptics';
import { IonBackButton, IonContent, IonHeader, IonTitle, IonToolbar, ModalController, NavController } from '@ionic/angular/standalone';
import { Environment, ENVIRONMENT } from "@qid/env";
import { SexyModule } from "@qid/sexy";
import { SuperButtonComponent } from "@qid/super";
import { ScannerTemplateOptions } from "../../../../models/scanner-template-options";
import { AddTemplateOptionComponent } from "../../components/add-template-option/add-template-option.component";
import { ScannerApiService } from '../../services/scanner-api.service';


@Component({
  selector: 'app-link-scanner',
  templateUrl: './link-scanner.page.html',
  styleUrls: ['./link-scanner.page.scss'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    SexyModule,
    SuperButtonComponent,
    IonHeader,
    IonToolbar,
    IonBackButton,
    IonTitle,
    IonContent
  ]
})

export class LinkScannerPage implements OnInit, OnDestroy {

  BASE_URL = this.environment.collectUrl;
  message: string = null;
  scannerTemplateOptions: ScannerTemplateOptions[] = [];
  qrCode: string;
  private scanListener: PluginListenerHandle | null = null;
  private handlingScan = false;
  private pageActive = false;

  constructor(
    private scannerService: ScannerApiService,
    private modalController: ModalController,
    private navController: NavController,
    @Inject(ENVIRONMENT) private environment: Environment
  ) {
  }

  ngOnInit() {

  }

  ionViewWillEnter() {
    this.pageActive = true;
    try {
      this.message = null;
      this.startScan();
    } catch (e) {
      this.message = e;
    }
  }


  ionViewWillLeave() {
    this.closeCamera();
  }

  ionViewDidLeave() {
    this.closeCamera();
  }

  ngOnDestroy() {
    this.closeCamera();
  }

  private closeCamera() {
    this.pageActive = false;
    this.stopScan();
  }


  private async startScan() {
    if (!this.pageActive || !Capacitor.isNativePlatform()) {
      return
    }
    const permission = await BarcodeScanner.checkPermissions();
    if (permission.camera !== 'granted') {
      const requested = await BarcodeScanner.requestPermissions();
      if (!this.pageActive || requested.camera !== 'granted') {
        return;
      }
    }
    if (!this.pageActive) return;

    await this.stopScan();
    if (!this.pageActive) return;
    this.handlingScan = false;
    document.body.classList.add('barcode-scanner-active');
    this.scanListener = await BarcodeScanner.addListener('barcodesScanned', async ({ barcodes }) => {
      if (this.handlingScan) {
        return;
      }
      this.handlingScan = true;
      const content = barcodes[0]?.rawValue;
      await this.stopScan();
      if (!this.pageActive) {
        return;
      }
      if (!content) {
        this.startScan();
        return;
      }
      try {
        await Haptics.impact({ style: ImpactStyle.Heavy });
        await this.onScan(await this.getQRCodeFromUrl(content));
      } catch (e) {
        this.message = typeof e === 'string' ? e : 'Invalid QR';
      }
    });
    if (!this.pageActive) {
      await this.stopScan();
      return;
    }
    await BarcodeScanner.startScan({ formats: [BarcodeFormat.QrCode] });
    if (!this.pageActive) {
      await this.stopScan();
    }
  }

  private async stopScan() {
    document.body.classList.remove('barcode-scanner-active');
    await this.scanListener?.remove();
    this.scanListener = null;
    try {
      await BarcodeScanner.stopScan();
    } catch {
      // already stopped
    }
  }

  async onScan(code: string) {

    this.qrCode = code;

    const check = await this.scannerService.getScannerByQr(code);

    if (check) {
      this.message = 'This QR Code is already linked';
      if (this.pageActive) {
        this.startScan();
      }
      return;

    }

    const modal = await this.modalController.create({
      component: AddTemplateOptionComponent,
      componentProps: {
        qrCode: code,
      },
    });
    await modal.present();
    const res = await modal.onDidDismiss();
    if (res.data.scanner) {
      this.back();
    }

  }


  async getQRCodeFromUrl(url: string) {

    if (url.indexOf(this.BASE_URL) < 0) {
      throw 'Invalid QR';
    }

    const qrCode = url.slice(this.BASE_URL.length, url.length);
    if (!/^\d+$/.test(qrCode)) {
      throw 'Invalid QR';
    }

    return qrCode;
  }


  async back() {
    this.closeCamera();
    this.navController.back();
  }


  generateVirtualQr = async () => {
    let code = new Date().getTime() + '' + (+(Math.random() * 10).toFixed(0) + 10);
    return this.onScan(code);
  };


}

